import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import {
  checkRedemptionEligibility,
  qPatientPackageItems,
  qPatientPackageRedemptions,
  qPatientPackagesForPatient,
  type PatientPackageItemRow,
  type PatientPackageRedemptionRow,
  type PatientPackageWithBalanceRow,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../adapters/powersync';
import { useFormatMoney } from '../hooks/useFormatMoney';
import {
  redeemPatientPackageItem,
  reversePatientPackageRedemption,
} from '../services/patient-package-redemption-service';
import {
  activatePatientPackage,
  reactivatePatientPackage,
  suspendPatientPackage,
} from '../services/patient-package-service';
import { NavIcon } from './shared';

// PRD.262 — Patient sidebar Packages panel with inline redemption.
//
// Read + redeem: package cards show remaining benefit balances and a Redeem
// action that opens an INLINE confirmation panel inside the drawer (not a
// modal). Redemption history and admin reverse / activate / suspend controls
// live here too.

interface SidebarPackagesPanelProps {
  patientId: string;
  /** Current user id (redeemed_by / actor on status changes). */
  userId?: string | null;
  /** Admin/billing gate for reversal and manual status controls. */
  isAdmin?: boolean;
  /** Encounter in context (from the URL) — enables "Attach to Today's Visit". */
  activeEncounterId?: string | null;
  /** When provided, renders a "View All Packages" CTA (sidebar → full page). */
  onViewAll?: () => void;
  /** PRD.263 — focus/expand this package when opened from the global page. */
  initialPatientPackageId?: string | null;
}

const cardStyle: CSSProperties = {
  borderRadius: 'var(--radius-lg)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  padding: 'var(--sp-4)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-3)',
};

const sectionTitleStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-2)',
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text)',
};

export function SidebarPackagesPanel({
  patientId,
  userId = null,
  isAdmin = false,
  activeEncounterId = null,
  onViewAll,
  initialPatientPackageId = null,
}: SidebarPackagesPanelProps) {
  const fmt = useFormatMoney();
  const { db } = usePowerSyncDb();
  const { data: packages = [] } = usePowerSyncQuery<PatientPackageWithBalanceRow>(
    qPatientPackagesForPatient(patientId),
  );
  const { data: redemptions = [] } = usePowerSyncQuery<PatientPackageRedemptionRow>(
    qPatientPackageRedemptions(patientId),
  );

  const today = new Date().toISOString().slice(0, 10);
  const activeCount = packages.filter((p) => p.redemption_status === 'active').length;
  const partiallyPaidCount = packages.filter((p) => p.payment_status === 'partially_paid').length;
  const outstandingCents = packages.reduce((sum, p) => sum + Math.max(0, p.balance_cents ?? 0), 0);
  const redeemedToday = redemptions.filter(
    (r) => r.status === 'posted' && (r.redeemed_at ?? '').slice(0, 10) === today,
  ).length;

  const packageNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of packages) m.set(p.id, p.package_name_snapshot ?? 'Package');
    return m;
  }, [packages]);

  return (
    <>
      {/* Summary */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={sectionTitleStyle}>
            <NavIcon name="shopping-bag" /> Patient Packages
          </span>
          {onViewAll && (
            <button
              onClick={onViewAll}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-primary)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              View All <NavIcon name="chevron-right" />
            </button>
          )}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 'var(--sp-2)',
          }}
        >
          <SummaryChip label="Active" value={String(activeCount)} tone="success" />
          <SummaryChip label="Partially Paid" value={String(partiallyPaidCount)} tone="warning" />
          <SummaryChip label="Outstanding" value={fmt(outstandingCents / 100)} tone="info" />
          <SummaryChip label="Redeemed Today" value={String(redeemedToday)} tone="info" />
        </div>
      </div>

      {packages.length === 0 ? (
        <div style={cardStyle}>
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
            No packages purchased yet. Add a package to an invoice to sell one.
          </div>
        </div>
      ) : (
        packages.map((pkg) => (
          <PackageCard
            key={pkg.id}
            pkg={pkg}
            fmt={fmt}
            db={db}
            userId={userId}
            isAdmin={isAdmin}
            activeEncounterId={activeEncounterId}
            today={today}
            focused={initialPatientPackageId === pkg.id}
          />
        ))
      )}

      <RedemptionHistory
        redemptions={redemptions}
        packageNameById={packageNameById}
        isAdmin={isAdmin}
        db={db}
        userId={userId}
      />

      {onViewAll && (
        <button
          onClick={onViewAll}
          style={{
            height: 36,
            padding: '0 14px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-primary)',
            background: 'var(--bg-surface)',
            color: 'var(--color-primary)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <NavIcon name="shopping-bag" /> View All Packages
        </button>
      )}
    </>
  );
}

type DbLike = ReturnType<typeof usePowerSyncDb>['db'];

function PackageCard({
  pkg,
  fmt,
  db,
  userId,
  isAdmin,
  activeEncounterId,
  today,
  focused = false,
}: {
  pkg: PatientPackageWithBalanceRow;
  fmt: (v: number | string | null | undefined) => string;
  db: DbLike;
  userId: string | null;
  isAdmin: boolean;
  activeEncounterId: string | null;
  today: string;
  focused?: boolean;
}) {
  const { data: items = [] } = usePowerSyncQuery<PatientPackageItemRow>(
    qPatientPackageItems(pkg.id),
  );
  const payment = paymentStatusMeta(pkg.payment_status);
  const redemption = redemptionStatusMeta(pkg.redemption_status);

  const [redeemItemId, setRedeemItemId] = useState<string | null>(null);
  const [statusReason, setStatusReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuspend, setShowSuspend] = useState(false);

  // PRD.263 — when opened from the global page, scroll the focused card into
  // view once.
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focused && cardRef.current) {
      cardRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [focused]);

  const isExpired = !!pkg.valid_until && pkg.valid_until < today;
  const canRedeemPackage = pkg.redemption_status === 'active' && !isExpired;

  const runStatus = async (fn: () => Promise<{ ok: boolean; error?: string }>) => {
    if (!db || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (!res.ok) setError(res.error ?? 'Action failed.');
      else {
        setShowSuspend(false);
        setStatusReason('');
      }
    } catch {
      setError('Action failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={cardRef}
      style={
        focused
          ? {
              ...cardStyle,
              borderColor: 'var(--color-primary)',
              boxShadow: '0 0 0 1px var(--color-primary)',
            }
          : cardStyle
      }
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-2)' }}>
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          {pkg.package_name_snapshot ?? 'Package'}
        </span>
        <StatusPill label={payment.label} bg={payment.bg} color={payment.color} />
      </div>

      {/* Paid / Total / Balance */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Row
          label="Paid"
          value={`${fmt((pkg.paid_cents ?? 0) / 100)} / ${fmt((pkg.grand_cents ?? 0) / 100)}`}
        />
        <Row label="Balance" value={fmt(Math.max(0, pkg.balance_cents ?? 0) / 100)} emphasize />
      </div>

      {/* Redemption + validity */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          Redemption:
        </span>
        <StatusPill label={redemption.label} bg={redemption.bg} color={redemption.color} />
        {pkg.valid_until && (
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: isExpired ? 'var(--color-danger, #dc2626)' : 'var(--text-muted)',
              marginLeft: 'auto',
            }}
          >
            {isExpired ? 'Expired' : 'Valid until'} {pkg.valid_until}
          </span>
        )}
      </div>

      {/* Partial-payment warning — non-blocking (PRD.262 §4). */}
      {pkg.payment_status === 'partially_paid' && pkg.redemption_status === 'active' && (
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-warning-text)',
            background: 'var(--color-warning-bg)',
            borderRadius: 'var(--radius-sm)',
            padding: '4px 8px',
          }}
        >
          This package is partially paid. Balance: {fmt(Math.max(0, pkg.balance_cents ?? 0) / 100)}.
        </div>
      )}
      {pkg.redemption_status === 'pending_payment' && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          This package is not active yet. Record a payment or activate it before redemption.
        </div>
      )}

      {/* Included benefit balances + redeem actions */}
      {items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Included benefits
          </span>
          {items.map((it) => {
            const remaining = it.remaining_quantity ?? 0;
            const used = it.status === 'fully_redeemed' || remaining <= 0;
            const open = redeemItemId === it.id;
            return (
              <div key={it.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 'var(--sp-2)',
                  }}
                >
                  <span
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--text)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {it.item_name_snapshot ?? 'Item'}
                  </span>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-2)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                      {formatQty(remaining)} left
                    </span>
                    {used ? (
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted)',
                          fontWeight: 'var(--font-weight-medium)',
                        }}
                      >
                        Used
                      </span>
                    ) : canRedeemPackage ? (
                      <button
                        type="button"
                        onClick={() => {
                          setError(null);
                          setRedeemItemId(open ? null : it.id);
                        }}
                        style={miniButton(open)}
                      >
                        {open ? 'Close' : 'Redeem'}
                      </button>
                    ) : null}
                  </span>
                </div>
                {open && (
                  <InlineRedeemPanel
                    item={it}
                    pkg={pkg}
                    db={db}
                    userId={userId}
                    activeEncounterId={activeEncounterId}
                    today={today}
                    onDone={() => setRedeemItemId(null)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Admin status controls (PRD.262 §12). */}
      {isAdmin &&
        (pkg.redemption_status === 'pending_payment' ||
          pkg.redemption_status === 'active' ||
          pkg.redemption_status === 'suspended') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
              {pkg.redemption_status === 'pending_payment' && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    db &&
                    runStatus(() =>
                      activatePatientPackage({
                        db,
                        patientPackageId: pkg.id,
                        userId,
                        reason: statusReason,
                      }),
                    )
                  }
                  style={miniButton(false)}
                >
                  Activate
                </button>
              )}
              {pkg.redemption_status === 'active' && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setShowSuspend((v) => !v)}
                  style={miniButton(showSuspend)}
                >
                  Suspend
                </button>
              )}
              {pkg.redemption_status === 'suspended' && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    db &&
                    runStatus(() =>
                      reactivatePatientPackage({ db, patientPackageId: pkg.id, userId }),
                    )
                  }
                  style={miniButton(false)}
                >
                  Reactivate
                </button>
              )}
            </div>
            {showSuspend && (
              <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                <input
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  placeholder="Reason (required)"
                  style={inlineInput}
                />
                <button
                  type="button"
                  disabled={busy || !statusReason.trim()}
                  onClick={() =>
                    db &&
                    runStatus(() =>
                      suspendPatientPackage({
                        db,
                        patientPackageId: pkg.id,
                        userId,
                        reason: statusReason,
                      }),
                    )
                  }
                  style={miniButton(true)}
                >
                  Confirm
                </button>
              </div>
            )}
          </div>
        )}

      {error && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger, #dc2626)' }}>
          {error}
        </div>
      )}
    </div>
  );
}

function InlineRedeemPanel({
  item,
  pkg,
  db,
  userId,
  activeEncounterId,
  today,
  onDone,
}: {
  item: PatientPackageItemRow;
  pkg: PatientPackageWithBalanceRow;
  db: DbLike;
  userId: string | null;
  activeEncounterId: string | null;
  today: string;
  onDone: () => void;
}) {
  const remaining = item.remaining_quantity ?? 0;
  const itemType = (item.item_type_snapshot ?? '').toLowerCase();
  // PRD.262 — medicine/product benefits are fulfilled by Pharmacy (batch +
  // stock-out happen there), not from this panel. A small inline hint keeps the
  // user informed without a modal.
  const fulfillmentHint =
    itemType === 'medicine'
      ? 'After redeeming, this is sent to Pharmacy to dispense. Stock is deducted there — no batch needed here.'
      : itemType === 'product'
        ? 'After redeeming, this is sent to Pharmacy for release. Stock is deducted there.'
        : null;
  const [qty, setQty] = useState('1');
  const [notes, setNotes] = useState('');
  const [attachToVisit, setAttachToVisit] = useState<boolean>(!!activeEncounterId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Client-side eligibility mirrors the server check for a friendly message;
  // the service re-validates + guards over-redemption transactionally.
  const eligibility = checkRedemptionEligibility({
    deletedAt: pkg.deleted_at,
    redemptionStatus: pkg.redemption_status,
    paymentStatus: pkg.payment_status,
    validUntil: pkg.valid_until,
    itemStatus: item.status,
    remaining,
    quantity: Number(qty),
    today,
  });

  const submit = async () => {
    if (!db || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await redeemPatientPackageItem({
        db,
        userId,
        input: {
          patientPackageId: pkg.id,
          patientPackageItemId: item.id,
          quantity: Number(qty),
          encounterId: attachToVisit ? activeEncounterId : null,
          notes: notes.trim() || null,
        },
      });
      if (!res.ok) setError(res.error ?? 'Redemption failed.');
      else onDone();
    } catch {
      setError('Redemption failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface-2, #f8fafc)',
        padding: 'var(--sp-3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
      }}
    >
      <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semi)' }}>
        Redeem {item.item_name_snapshot ?? 'Item'}
      </span>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
        Remaining: {formatQty(remaining)}
      </span>
      {fulfillmentHint && (
        <div
          style={{
            display: 'flex',
            gap: 6,
            alignItems: 'flex-start',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-info-text, #1e40af)',
            background: 'var(--color-info-bg, #dbeafe)',
            borderRadius: 'var(--radius-sm)',
            padding: '6px 8px',
          }}
        >
          <NavIcon name="info" />
          <span>{fulfillmentHint}</span>
        </div>
      )}
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', width: 60 }}>
          Quantity
        </span>
        <input
          type="number"
          min="1"
          step="any"
          max={remaining}
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          style={{ ...inlineInput, width: 80 }}
        />
      </label>
      {activeEncounterId && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <input
            type="checkbox"
            checked={attachToVisit}
            onChange={(e) => setAttachToVisit(e.target.checked)}
            style={{ accentColor: 'var(--color-primary)' }}
          />
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text)' }}>
            Attach to today&apos;s visit
          </span>
        </label>
      )}
      <input
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes (optional)"
        style={inlineInput}
      />
      {!eligibility.ok && (
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger, #dc2626)' }}>
          {eligibility.error}
        </span>
      )}
      {error && (
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger, #dc2626)' }}>
          {error}
        </span>
      )}
      <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onDone} disabled={busy} style={miniButton(false)}>
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !eligibility.ok}
          style={miniButton(true)}
        >
          {busy ? 'Redeeming…' : 'Redeem'}
        </button>
      </div>
    </div>
  );
}

function RedemptionHistory({
  redemptions,
  packageNameById,
  isAdmin,
  db,
  userId,
}: {
  redemptions: PatientPackageRedemptionRow[];
  packageNameById: Map<string, string>;
  isAdmin: boolean;
  db: DbLike;
  userId: string | null;
}) {
  const [reverseId, setReverseId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (redemptions.length === 0) return null;
  const recent = redemptions.slice(0, 15);

  const doReverse = async (id: string) => {
    if (!db || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await reversePatientPackageRedemption({ db, redemptionId: id, reason, userId });
      if (!res.ok) setError(res.error ?? 'Reversal failed.');
      else {
        setReverseId(null);
        setReason('');
      }
    } catch {
      setError('Reversal failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={sectionTitleStyle}>
        <NavIcon name="clock" /> Redemption History
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {recent.map((r, i) => {
          const reversed = r.status === 'reversed';
          return (
            <div
              key={r.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                padding: 'var(--sp-2) 0',
                borderBottom: i < recent.length - 1 ? '1px solid var(--border)' : 'none',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-2)' }}>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                    textDecoration: reversed ? 'line-through' : 'none',
                  }}
                >
                  {r.item_name_snapshot ?? 'Item'} x{formatQty(r.redeemed_quantity)}
                </span>
                {reversed ? (
                  <StatusPill
                    label="Reversed"
                    bg="var(--bg-surface-2, #f3f4f6)"
                    color="var(--text-muted)"
                  />
                ) : (
                  isAdmin && (
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        setReverseId(reverseId === r.id ? null : r.id);
                      }}
                      style={miniButton(reverseId === r.id)}
                    >
                      Reverse
                    </button>
                  )
                )}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                {(r.redeemed_at ?? '').replace('T', ' ').slice(0, 16)}
                {r.patient_package_id
                  ? ` · ${packageNameById.get(r.patient_package_id) ?? 'Package'}`
                  : ''}
              </div>
              {reverseId === r.id && !reversed && (
                <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 4 }}>
                  <input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Reason (required)"
                    style={inlineInput}
                  />
                  <button
                    type="button"
                    disabled={busy || !reason.trim()}
                    onClick={() => doReverse(r.id)}
                    style={miniButton(true)}
                  >
                    Confirm
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {error && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger, #dc2626)' }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ─── Shared bits ───

const inlineInput: CSSProperties = {
  flex: 1,
  minWidth: 0,
  height: 30,
  padding: '0 8px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  fontFamily: 'var(--font-family)',
};

function miniButton(active: boolean): CSSProperties {
  return {
    padding: '3px 10px',
    borderRadius: 'var(--radius-sm)',
    border: `1px solid ${active ? 'var(--color-primary)' : 'var(--border)'}`,
    background: active ? 'var(--color-primary)' : 'var(--bg-surface)',
    color: active ? '#fff' : 'var(--color-primary)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-semi)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
}

function Row({ label, value, emphasize }: { label: string; value: string; emphasize?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-2)' }}>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>{label}</span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: emphasize ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
          color: 'var(--text)',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function StatusPill({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 'var(--radius-full, 9999px)',
        background: bg,
        color,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

function SummaryChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'info' | 'warning' | 'success';
}) {
  const palette = {
    info: { bg: 'var(--color-info-bg, #dbeafe)', color: 'var(--color-info-text, #1e40af)' },
    warning: { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' },
    success: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' },
  }[tone];
  return (
    <div
      style={{
        padding: 'var(--sp-2) var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        background: palette.bg,
        color: palette.color,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        textAlign: 'center',
      }}
    >
      <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, lineHeight: 1.1 }}>
        {value}
      </span>
      <span
        style={{
          fontSize: 9,
          fontWeight: 'var(--font-weight-semi)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          opacity: 0.85,
        }}
      >
        {label}
      </span>
    </div>
  );
}

function paymentStatusMeta(status: string | null): { label: string; bg: string; color: string } {
  switch (status) {
    case 'paid':
      return { label: 'Paid', bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' };
    case 'partially_paid':
      return {
        label: 'Partially Paid',
        bg: 'var(--color-warning-bg)',
        color: 'var(--color-warning-text)',
      };
    case 'voided':
      return { label: 'Voided', bg: 'var(--bg-surface-2, #f3f4f6)', color: 'var(--text-muted)' };
    default:
      return { label: 'Unpaid', bg: 'var(--bg-surface-2, #f3f4f6)', color: 'var(--text-muted)' };
  }
}

function redemptionStatusMeta(status: string | null): { label: string; bg: string; color: string } {
  switch (status) {
    case 'active':
      return { label: 'Active', bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' };
    case 'suspended':
      return {
        label: 'Suspended',
        bg: 'var(--color-warning-bg)',
        color: 'var(--color-warning-text)',
      };
    case 'fully_redeemed':
      return {
        label: 'Fully Redeemed',
        bg: 'var(--color-info-bg, #dbeafe)',
        color: 'var(--color-info-text, #1e40af)',
      };
    case 'voided':
      return { label: 'Voided', bg: 'var(--bg-surface-2, #f3f4f6)', color: 'var(--text-muted)' };
    default:
      return {
        label: 'Pending Payment',
        bg: 'var(--color-info-bg, #dbeafe)',
        color: 'var(--color-info-text, #1e40af)',
      };
  }
}

function formatQty(n: number | null): string {
  if (n === null || n === undefined) return '0';
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(3)));
}
